import Decimal from 'decimal.js'

const ZERO_DECIMAL_CURRENCIES = new Set(['CLP', 'JPY', 'PYG'])

const LOCALE = 'pt-BR'
const LEDGER_DECIMAL_PLACES = 3

const symbolCache = new Map<string, string>()

export function currencyFractionDigits(currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase()) ? 0 : 2
}

function currencySymbol(currency: string): string {
  const cached = symbolCache.get(currency)
  if (cached) {
    return cached
  }
  const parts = new Intl.NumberFormat(LOCALE, { style: 'currency', currency }).formatToParts(0)
  const symbol = parts.find((part) => part.type === 'currency')?.value ?? currency
  symbolCache.set(currency, symbol)
  return symbol
}

function decimalSeparator(): string {
  const parts = new Intl.NumberFormat(LOCALE).formatToParts(1.1)
  return parts.find((part) => part.type === 'decimal')?.value ?? ','
}

function groupInteger(digits: string): string {
  return new Intl.NumberFormat(LOCALE, { useGrouping: true, maximumFractionDigits: 0 }).format(
    BigInt(digits),
  )
}

export type MoneyParts = {
  sign: string
  symbol: string
  integer: string
  fraction: string
}

export function formatMoneyParts(
  amount: string,
  currency: string,
  fractionDigits = currencyFractionDigits(currency),
): MoneyParts {
  const digits = Math.max(0, Math.min(20, Math.trunc(fractionDigits)))

  let decimal: Decimal
  try {
    decimal = new Decimal(amount)
  } catch {
    return { sign: '', symbol: currency, integer: amount, fraction: '' }
  }

  if (!decimal.isFinite()) {
    return { sign: '', symbol: currency, integer: amount, fraction: '' }
  }

  const rounded = decimal.toDecimalPlaces(digits, Decimal.ROUND_HALF_UP)
  const fixed = rounded.abs().toFixed(digits)
  const [integerDigits, fractionPart = ''] = fixed.split('.')

  return {
    sign: rounded.isNegative() && !rounded.isZero() ? '-' : '',
    symbol: currencySymbol(currency),
    integer: groupInteger(integerDigits ?? '0'),
    fraction: digits > 0 ? `${decimalSeparator()}${fractionPart}` : '',
  }
}

export function formatMoney(amount: string, currency: string): string {
  const parts = formatMoneyParts(amount, currency)
  return [`${parts.sign}${parts.symbol}`, `${parts.integer}${parts.fraction}`].join(' ')
}

export function formatCurrencyInput(
  amount: string | number | null | undefined,
  currency: string,
): string {
  let decimal: Decimal
  try {
    decimal = new Decimal(amount ?? 0)
  } catch {
    decimal = new Decimal(0)
  }

  if (!decimal.isFinite()) {
    decimal = new Decimal(0)
  }

  return formatMoney(decimal.toFixed(currencyFractionDigits(currency)), currency)
}

export function currencyInputToLedgerAmount(value: string, currency: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.length === 0) {
    return new Decimal(0).toFixed(LEDGER_DECIMAL_PLACES)
  }

  const fractionDigits = currencyFractionDigits(currency)
  const amount = new Decimal(digits).div(new Decimal(10).pow(fractionDigits))
  return amount.toFixed(LEDGER_DECIMAL_PLACES)
}
